import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { 
  Loader2, Save, Phone, Lock, Eye, EyeOff, AlertCircle, CheckCircle, 
  DollarSign // <-- NUEVO IMPORT
} from "lucide-react";
// NUEVOS IMPORTS PARA SELECT
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateOwnerCurrency, updateOwnerProfile } from "@/lib/api"; // <-- NUEVO IMPORT
import { getCurrencyDisplayName } from "@/lib/currency";

interface OwnerAccountSettingsProps {
  owner: {
    id: string;
    businessName?: string | null;
    ownerName?: string | null;
    email?: string | null;
    phone?: string | null;
    currency?: string | null; // <-- NUEVO CAMPO
  };
}

export default function OwnerAccountSettings({ owner }: OwnerAccountSettingsProps) {
  // Estados para formulario de teléfono
  const [phone, setPhone] = useState(owner.phone || "");
  
  // Estados para formulario de contraseña
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Estados para feedback
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [phoneUpdated, setPhoneUpdated] = useState(false);

  // Estados para moneda
  const [currency, setCurrency] = useState(owner.currency || "CUP");
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false);
  const [currencyUpdated, setCurrencyUpdated] = useState(false);

  // Estados para perfil
  const [businessName, setBusinessName] = useState(owner.businessName || "");
  const [ownerName, setOwnerName] = useState(owner.ownerName || "");
  const [email, setEmail] = useState(owner.email || "");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileUpdated, setProfileUpdated] = useState(false);
  
  // Funciones helper para valores seguros
  const getSafeString = (value: string | null | undefined, defaultValue: string = "") => {
    return value || defaultValue;
  };
  
  const handleUpdatePhone = async () => {
    const currentPhone = owner.phone || "";
    if (phone === currentPhone) {
      toast({
        title: "Sin cambios",
        description: "El número de teléfono es el mismo",
      });
      return;
    }
    
    if (phone && !/^[\+]?[0-9\s\-\(\)]{8,}$/.test(phone)) {
      toast({
        title: "Teléfono inválido",
        description: "Por favor ingresa un número de teléfono válido",
        variant: "destructive",
      });
      return;
    }
    
    setIsUpdatingPhone(true);
    
    try {
      // 1. Actualizar en localStorage temporalmente
      const updatedOwner = { ...owner, phone };
      localStorage.setItem('currentOwner', JSON.stringify(updatedOwner));
      
      // 2. Actualizar en la base de datos via API
      const response = await fetch(`/api/owners/${owner.id}/phone`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Teléfono actualizado",
          description: result.message || "Tu número de teléfono ha sido actualizado correctamente",
        });
        
        setPhoneUpdated(true);
        setTimeout(() => setPhoneUpdated(false), 3000);
        
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.error || "No se pudo actualizar el teléfono",
          variant: "destructive",
        });
      }
      
    } catch (error) {
      toast({
        title: "Error de conexión",
        description: "No se pudo conectar con el servidor",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPhone(false);
    }
  };
  
  const handleChangePassword = async () => {
    // Validaciones
    if (!currentPassword) {
      toast({
        title: "Contraseña actual requerida",
        description: "Debes ingresar tu contraseña actual",
        variant: "destructive",
      });
      return;
    }
    
    if (!newPassword) {
      toast({
        title: "Nueva contraseña requerida",
        description: "Debes ingresar una nueva contraseña",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: "Contraseña muy corta",
        description: "La nueva contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Contraseñas no coinciden",
        description: "La confirmación de contraseña no coincide con la nueva contraseña",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword === currentPassword) {
      toast({
        title: "Contraseña igual",
        description: "La nueva contraseña debe ser diferente a la actual",
        variant: "destructive",
      });
      return;
    }
    
    setIsUpdatingPassword(true);
    
    try {
      // Intentar actualizar via API
      const response = await fetch(`/api/owners/${owner.id}/password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      
      if (response.ok) {
        toast({
          title: "Contraseña cambiada",
          description: "Tu contraseña ha sido actualizada correctamente",
        });
        
        // Limpiar formulario
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.error || "No se pudo cambiar la contraseña",
          variant: "destructive",
        });
      }
      
    } catch (error) {
      toast({
        title: "Error de conexión",
        description: "No se pudo conectar con el servidor",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleCurrencyChange = async (newCurrency: string) => {
    if (newCurrency === currency) {
      return;
    }
    
    setIsUpdatingCurrency(true);
    try {
      // 1. Actualizar en localStorage temporalmente
      const updatedOwner = { ...owner, currency: newCurrency };
      localStorage.setItem('currentOwner', JSON.stringify(updatedOwner));
      
      // 2. Actualizar en la base de datos via API
      const result = await updateOwnerCurrency(owner.id, newCurrency);
      
      if (result.success) {
        toast({
          title: "Moneda actualizada",
          description: result.message || "Tu moneda predeterminada ha sido actualizada correctamente",
        });
        
        setCurrency(newCurrency);
        setCurrencyUpdated(true);
        setTimeout(() => setCurrencyUpdated(false), 3000);
      } else {
        // Si la API devuelve un error, result.success será false
        // y puede que tenga propiedad error o message
        const errorMessage = (result as any).error || result.message || "No se pudo actualizar la moneda";
        throw new Error(errorMessage);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudo actualizar la moneda",
        variant: "destructive",
      });
      
      // Revertir el cambio en el estado local
      const currentOwner = JSON.parse(localStorage.getItem('currentOwner') || '{}');
      if (currentOwner.id === owner.id && currentOwner.currency) {
        setCurrency(currentOwner.currency);
      }
    } finally {
      setIsUpdatingCurrency(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const result = await updateOwnerProfile(owner.id, {
        businessName: businessName || undefined,
        ownerName: ownerName || undefined,
        email: email || undefined,
      });
      const updatedOwner = { ...owner, ...result.owner };
      localStorage.setItem("currentOwner", JSON.stringify(updatedOwner));
      toast({
        title: "Perfil actualizado",
        description: "Los cambios se han guardado correctamente.",
      });
      setProfileUpdated(true);
      setTimeout(() => setProfileUpdated(false), 3000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "No se pudo actualizar el perfil",
        variant: "destructive",
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Información de la cuenta */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Información de la Cuenta</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="business-name">Nombre de la Empresa</Label>
            <Input 
              id="business-name" 
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="owner-name">Nombre del Dueño</Label>
            <Input 
              id="owner-name" 
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input 
              id="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="owner-id">ID del Dueño</Label>
            <Input 
              id="owner-id" 
              value={owner.id || "No disponible"} 
              disabled 
              className="bg-gray-50"
            />
          </div>
        </div>
        <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
          {isSavingProfile ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
          ) : profileUpdated ? (
            <><CheckCircle className="h-4 w-4 mr-2 text-green-500" /> Guardado</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Guardar Cambios</>
          )}
        </Button>
      </div>
      
      <Separator />
      
      {/* Actualizar teléfono */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Número de Teléfono
        </h3>
        <div className="space-y-2">
          <Label htmlFor="phone">Teléfono de contacto</Label>
          <div className="flex gap-2">
            <Input
              id="phone"
              type="tel"
              placeholder="+53 5xxx xxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleUpdatePhone}
              disabled={isUpdatingPhone || phone === (owner.phone || "")}
            >
              {isUpdatingPhone ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : phoneUpdated ? (
                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {phoneUpdated ? "Guardado" : "Guardar"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Usaremos este número para contactarte si es necesario
          </p>
        </div>
      </div>
      
      <Separator />

       {/* Configurar moneda */}
       <div className="space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Moneda Predeterminada
        </h3>
        <div className="space-y-2">
          <Label htmlFor="currency">Selecciona tu moneda</Label>
          <div className="flex gap-2">
            <Select
              value={currency}
              onValueChange={handleCurrencyChange}
              disabled={isUpdatingCurrency}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecciona moneda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CUP">{getCurrencyDisplayName('CUP')}</SelectItem>
                <SelectItem value="USD">{getCurrencyDisplayName('USD')}</SelectItem>
                <SelectItem value="EUR">{getCurrencyDisplayName('EUR')}</SelectItem>
                <SelectItem value="L">{getCurrencyDisplayName('L')}</SelectItem>
                <SelectItem value="MXN">{getCurrencyDisplayName('MXN')}</SelectItem>
              </SelectContent>
            </Select>
            {isUpdatingCurrency ? (
              <Loader2 className="h-5 w-5 animate-spin my-auto" />
            ) : currencyUpdated ? (
              <CheckCircle className="h-5 w-5 text-green-500 my-auto" />
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            Esta moneda se usará para mostrar precios en todas tus tiendas
          </p>
        </div>
      </div>
      
      <Separator />
      
      {/* Cambiar contraseña */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Cambiar Contraseña
        </h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Contraseña Actual</Label>
            <div className="relative">
              <Input
                id="current-password"
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Ingresa tu contraseña actual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new-password">Nueva Contraseña</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? "text" : "password"}
                placeholder="Ingresa tu nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Mínimo 6 caracteres
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirmar Nueva Contraseña</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirma tu nueva contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div className="pt-4">
            <Button 
              onClick={handleChangePassword}
              disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full"
            >
              {isUpdatingPassword ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              Cambiar Contraseña
            </Button>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <h4 className="font-medium text-amber-800">Recomendaciones de Seguridad</h4>
            </div>
            <ul className="text-sm text-amber-700 mt-2 space-y-1">
              <li>• Usa al menos 6 caracteres</li>
              <li>• Combina letras, números y símbolos</li>
              <li>• No uses contraseñas que hayas usado antes</li>
              <li>• No compartas tu contraseña con nadie</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Información adicional */}
      <div className="text-center text-sm text-muted-foreground pt-4 border-t">
        <p>
          ¿Necesitas ayuda o quieres cambiar otros datos?{" "}
          <button 
            className="text-primary hover:underline"
            onClick={() => {
              toast({
                title: "Contactar soporte",
                description: "Envía un correo a soporte@mercadocubano.cu",
              });
            }}
          >
            Contacta al soporte técnico
          </button>
        </p>
      </div>
    </div>
  );
}